import { useState, type FormEvent } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { AuditEntry, TeamMember, TeamRole } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { ConfirmDialog, EmptyState, ErrorState, Loading, Segmented, StatusPill, arrowNav, useTitle } from '../../ui/bits';
import { useToast } from '../../ui/toast';
import { Icon } from '../../ui/Icon';
import { DotMeter, PageHead, Ring, Sheet, Stat } from '../../ui/viz';
import { STATE_ART } from '../../lib/voice.js';
import { count, initials } from '../../lib/format.js';
import { RiderProfileDrawer } from '../riders/ProfileDrawer';
import { ItemMenu } from '../zones/Menu';
import {
  MAX_EMAIL,
  MAX_NAME,
  ROLE_LABEL,
  ROLE_OPTIONS,
  appendAudit,
  auditView,
  memberMeta,
  memberName,
  removeBlock,
  removeCopy,
  riderState,
  sortMembers,
  teamActions,
  teamKeys,
  teamProblem,
  validateInvite,
} from './lib.js';
import './team.css';

type Tab = 'platform_admin' | 'rider' | 'activity';
const TABS: { value: Tab; label: string }[] = [
  { value: 'platform_admin', label: 'Admins' },
  { value: 'rider', label: 'Riders' },
  { value: 'activity', label: 'Activity' },
];

const actions = teamActions(api);

/** Ops · Team: who can open Ops, who rides, and what changed (the audit trail). */
export function OpsTeamPage() {
  useTitle('Team');
  const [tab, setTab] = useState<Tab>('platform_admin');
  const [inviting, setInviting] = useState(false);
  const admins = useQuery({ queryKey: teamKeys.list('platform_admin'), queryFn: () => api.opsTeam('platform_admin') });
  const riders = useQuery({ queryKey: teamKeys.list('rider'), queryFn: () => api.opsTeam('rider') });
  const inviteRole: TeamRole = tab === 'rider' ? 'rider' : 'platform_admin';
  const existing = sortMembers((inviteRole === 'rider' ? riders.data : admins.data) ?? []);
  const n = (k: number | undefined, one: string, many: string) => (k === undefined ? null : `${count(k)} ${k === 1 ? one : many}`);

  return (
    <>
      <PageHead
        title="Team"
        meta={
          <>
            {[n(admins.data?.length, 'admin', 'admins'), n(riders.data?.length, 'rider', 'riders')].filter(Boolean).join(' · ')}
            <span className="tm-logged" title="Every change to people and shops is logged">
              <Icon name="check" /> Logged
            </span>
          </>
        }
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setInviting(true)}>
            <Icon name="plus" />
            Invite someone
          </button>
        }
      />

      <TeamStats admins={admins.data} riders={riders.data} />

      <div className="tabs" role="tablist" aria-label="Team" onKeyDown={(e) => arrowNav(e, '[role="tab"]')}>
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            id={`tm-tab-${t.value}`}
            aria-selected={tab === t.value}
            aria-controls="tm-panel"
            tabIndex={tab === t.value ? 0 : -1}
            className="pill tab chip-outline"
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div id="tm-panel" role="tabpanel" aria-labelledby={`tm-tab-${tab}`} className="stack s16">
        {tab === 'activity' ? <Activity /> : <People role={tab} query={tab === 'rider' ? riders : admins} onInvite={() => setInviting(true)} />}
      </div>

      <Sheet open={inviting} title="Invite someone" onClose={() => setInviting(false)}>
        <Invite role={inviteRole} existing={existing} onDone={() => setInviting(false)} />
      </Sheet>
    </>
  );
}

/** The team in figures: admins, riders online (one pip each), complete profiles, riders on a job. */
function TeamStats({ admins, riders }: { admins?: TeamMember[]; riders?: TeamMember[] }) {
  if (!admins && !riders) return null;
  const states = (riders ?? []).map(riderState);
  const total = states.length;
  const online = states.filter((s) => s.online).length;
  const busy = states.filter((s) => s.online && s.activeJobs > 0).length;
  const complete = states.filter((s) => s.complete).length;
  const onJob = states.filter((s) => s.activeJobs > 0).length;
  const pct = total ? Math.round((complete / total) * 100) : null;
  return (
    <div className="dv-stats">
      <Stat label="Admins" value={admins ? count(admins.length) : '—'} visual={<span className="dv-disc" aria-hidden="true"><Icon name="team" /></span>} />
      <Stat label="Riders online" value={riders ? `${count(online)} / ${count(total)}` : '—'} visual={total ? <DotMeter total={total} on={online} busy={busy} /> : undefined} />
      <Stat label="Profile complete" value={riders ? `${count(complete)} / ${count(total)}` : '—'} visual={<Ring pct={pct} tone={pct === null || pct >= 80 ? 'good' : pct >= 50 ? 'warn' : 'bad'} />} />
      <Stat label="On a job" value={riders ? count(onJob) : '—'} visual={<span className="dv-disc" aria-hidden="true"><Icon name="pin" /></span>} />
    </div>
  );
}

function People({ role, query, onInvite }: { role: TeamRole; query: UseQueryResult<TeamMember[]>; onInvite(): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const list = sortMembers(query.data ?? []);

  const revoke = useMutation({
    mutationFn: (m: TeamMember) => actions.revoke(m.userId, role),
    onSuccess: (_d, m) => {
      qc.setQueryData<TeamMember[]>(teamKeys.list(role), (xs) => xs?.filter((x) => x.userId !== m.userId));
      void qc.invalidateQueries({ queryKey: teamKeys.all });
      void qc.invalidateQueries({ queryKey: teamKeys.audit });
      toast(`${memberName(m)} is no longer ${role === 'platform_admin' ? 'an admin' : 'a rider'}.`);
      setRemoving(null);
      setProblem(null);
    },
    onError: (err) => {
      setRemoving(null);
      setProblem(teamProblem(err as { status?: number; message?: string }, 'remove', role, errorMessage));
    },
  });

  const title = role === 'platform_admin' ? 'Admins' : 'Riders';
  const copy = removing ? removeCopy(removing, role) : null;

  return (
    <>
      <section className="card dv-card dv-rise" aria-labelledby="tm-list-h" aria-busy={query.isFetching || undefined}>
        <div className="dv-card-h">
          <h2 id="tm-list-h">{title}</h2>
          {query.data ? <span className="dv-chip">{count(list.length)}</span> : null}
          <span className="grow" />
          <span className="dv-sub" title={role === 'platform_admin' ? 'People who can open PopCart Ops' : 'Riders go online once their profile is complete'}>
            <Icon name="clock" /> {role === 'platform_admin' ? 'Sessions end after 12 h' : 'Full profile to go online'}
          </span>
        </div>
        {problem ? (
          <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
            {problem}
          </p>
        ) : null}
        {query.isError && !query.data ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : !query.data ? (
          <Loading label={`Loading ${title.toLowerCase()}`} rows={2} />
        ) : list.length === 0 ? (
          <EmptyState
            art={role === 'rider' ? STATE_ART.noRiders : STATE_ART.noResults}
            title={role === 'rider' ? 'No riders yet' : 'No admins yet'}
            action={
              <button type="button" className="btn btn-secondary" onClick={onInvite}>
                <Icon name="plus" />
                {role === 'rider' ? 'Invite a rider' : 'Invite an admin'}
              </button>
            }
          >
            They sign in with their email.
          </EmptyState>
        ) : (
          <ul className="tm-list" aria-label={title}>
            {list.map((m, j) => {
              const block = removeBlock(m, role, list);
              const name = memberName(m);
              const r = role === 'rider' ? riderState(m) : null;
              return (
                <li key={m.userId} className="tm-row" style={{ ['--i' as string]: j }}>
                  <span className={r ? `avatar tm-av${r.online ? ' on' : ''}` : 'avatar tm-av'} aria-hidden="true">
                    {initials(m.name, m.email)}
                  </span>
                  <span className="tm-who">
                    <b>{name}</b>
                    {m.email && m.email !== name ? <span className="dv-sub tm-email">{m.email}</span> : null}
                  </span>
                  {r ? (
                    <span className="tm-pills" aria-label={`${r.profileLabel}, ${r.onlineLabel}, ${r.jobsLabel}`}>
                      <StatusPill tone={r.complete ? 'success' : 'warning'}>{r.profileLabel}</StatusPill>
                      <StatusPill tone={r.online ? 'success' : 'neutral'}>{r.onlineLabel}</StatusPill>
                      <StatusPill tone={r.activeJobs ? 'info' : 'neutral'}>{r.jobsLabel}</StatusPill>
                    </span>
                  ) : (
                    <span className="tm-pills">
                      <span className="dv-chip">{ROLE_LABEL[role]}</span>
                    </span>
                  )}
                  <span className="dv-sub tm-meta">{memberMeta(m)}</span>
                  <span className="tm-act">
                    {r ? (
                      <button type="button" className="btn btn-quiet" aria-label={`Open ${name}'s profile`} onClick={() => setProfileId(m.userId)}>
                        Profile
                      </button>
                    ) : null}
                  </span>
                  <ItemMenu
                    label={`More for ${name}`}
                    note={block}
                    noteId={`tm-block-${m.userId}`}
                    items={[
                      {
                        label: `Remove as ${ROLE_LABEL[role].toLowerCase()}`,
                        ariaLabel: `Remove ${name} as ${ROLE_LABEL[role].toLowerCase()}`,
                        icon: 'close',
                        tone: 'danger',
                        disabled: !!block || revoke.isPending,
                        describedBy: block ? `tm-block-${m.userId}` : undefined,
                        onSelect: () => {
                          setProblem(null);
                          setRemoving(m);
                        },
                      },
                    ]}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={removing !== null}
        title={copy?.title ?? ''}
        body={copy?.body ?? ''}
        confirmLabel={copy?.confirm ?? 'Remove'}
        busy={revoke.isPending}
        onConfirm={() => removing && revoke.mutate(removing)}
        onClose={() => setRemoving(null)}
      />
      {role === 'rider' ? <RiderProfileDrawer userId={profileId} name={list.find((m) => m.userId === profileId)?.name ?? null} onClose={() => setProfileId(null)} /> : null}
    </>
  );
}

function Invite({ role: tabRole, existing, onDone }: { role: TeamRole; existing: TeamMember[]; onDone(): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<TeamRole>(tabRole);
  const [errors, setErrors] = useState<Partial<Record<'email' | 'name' | 'role', string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  // The form follows the tab it opened from until someone picks a role by hand.
  const [lastTab, setLastTab] = useState(tabRole);
  if (lastTab !== tabRole) {
    setLastTab(tabRole);
    setRole(tabRole);
  }

  const invite = useMutation({
    mutationFn: (body: { email: string; role: TeamRole; name?: string }) => actions.invite(body),
    onSuccess: (m, body) => {
      void qc.invalidateQueries({ queryKey: teamKeys.all });
      void qc.invalidateQueries({ queryKey: teamKeys.audit });
      const who = memberName(m ?? { name: body.name ?? null, email: body.email });
      toast(body.role === 'platform_admin' ? `${who} is now an admin. We emailed them a link to PopCart Ops.` : `${who} is now a rider. We emailed them how to get the rider app.`);
      setEmail('');
      setName('');
      setErrors({});
      setFormError(null);
      onDone();
    },
    onError: (err) => setFormError(teamProblem(err as { status?: number; message?: string }, 'invite', role, errorMessage)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const r = validateInvite({ email, name, role }, role === tabRole ? existing : []);
    if (!r.ok) {
      setErrors(r.errors);
      return;
    }
    setErrors({});
    invite.mutate(r.value);
  };
  const help = ROLE_OPTIONS.find((o) => o.value === role)?.help;

  return (
    <form className="stack s16" aria-label="Invite someone" onSubmit={submit} noValidate>
      <div className="st-panel">
        <div className="field">
          <span className="flabel">Role</span>
          <Segmented<TeamRole> label="Role" value={role} options={ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} onChange={setRole} />
          {help ? <span className="help">{help}</span> : null}
        </div>
      </div>
      <div className="field">
        <label htmlFor="tm-email">Their email</label>
        <input
          id="tm-email"
          type="email"
          className="input"
          autoComplete="off"
          inputMode="email"
          maxLength={MAX_EMAIL}
          value={email}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? 'tm-email-err' : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors(({ email: _e, ...rest }) => rest);
          }}
        />
        {errors.email ? (
          <span id="tm-email-err" className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
            {errors.email}
          </span>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor="tm-name">Name (optional)</label>
        <input
          id="tm-name"
          className="input"
          autoComplete="off"
          maxLength={MAX_NAME}
          value={name}
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={errors.name ? 'tm-name-err' : undefined}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors(({ name: _n, ...rest }) => rest);
          }}
        />
        {errors.name ? (
          <span id="tm-name-err" className="pill st-error err-pill" style={{ whiteSpace: 'normal' }}>
            {errors.name}
          </span>
        ) : null}
      </div>
      <p className="dv-sub">New to PopCart? We make their account and email them what to do next.</p>
      {formError ? (
        <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
          {formError}
        </p>
      ) : null}
      <div className="st-savebar">
        <span className="grow" />
        <button type="submit" className="btn btn-primary" disabled={invite.isPending} aria-busy={invite.isPending || undefined}>
          <Icon name="plus" />
          {invite.isPending ? 'Sending…' : role === 'platform_admin' ? 'Invite admin' : 'Invite rider'}
        </button>
      </div>
    </form>
  );
}

function Activity() {
  const q = useInfiniteQuery({
    queryKey: teamKeys.audit,
    queryFn: ({ pageParam }) => actions.audit(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
  const rows = (q.data?.pages ?? []).reduce<AuditEntry[]>((acc, p) => appendAudit(acc, p.items), []);

  return (
    <section className="card dv-card dv-rise" aria-labelledby="tm-activity-h" aria-busy={q.isFetching || undefined}>
      <div className="dv-card-h">
        <h2 id="tm-activity-h">Activity</h2>
        {rows.length ? <span className="dv-chip">{count(rows.length)}{q.hasNextPage ? '+' : ''}</span> : null}
        <span className="grow" />
        <span className="dv-sub" title="Every change to people and shops, newest first. Nothing here can be edited or deleted.">
          <Icon name="check" /> Read-only log
        </span>
      </div>
      {q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Loading label="Loading activity" rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState art={STATE_ART.noResults} title="Nothing logged yet">
          Invites, role changes and shop changes show up here.
        </EmptyState>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">What</th>
                  <th scope="col">Change</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const v = auditView(e);
                  return (
                    <tr key={v.id}>
                      <td className="dv-sub tm-when">{v.when}</td>
                      <td>{v.who}</td>
                      <td>
                        <StatusPill tone={v.tone}>{v.label}</StatusPill>
                      </td>
                      <td>{v.summary}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {q.hasNextPage ? (
            <div className="row">
              <button type="button" className="btn" disabled={q.isFetchingNextPage} aria-busy={q.isFetchingNextPage || undefined} onClick={() => void q.fetchNextPage()}>
                {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : (
            <div className="dv-quiet">That's everything.</div>
          )}
          {q.isFetchNextPageError ? (
            <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
              {errorMessage(q.error, "Couldn't load more. Try again.")}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
